import { useState, useEffect } from 'react';

// Inspired by https://codepen.io/ashik4339/pen/XWNmOrm (moody, multi-layered gradients)
const TIME_SLOTS = {
  morning: {
    start: 5,
    end: 11,
    gradients: [
      // Golden sunrise, soft lavender/mauve haze
      'linear-gradient(120deg, #40306A 0%, #B79891 25%, #FFD89B 55%, #FFB0BB 80%, #7F7FD5 100%)',
      'linear-gradient(110deg, #614385 0%, #516395 28%, #FFECD2 60%, #FFC3A0 90%, #596164 100%)',
      'linear-gradient(120deg, #6A93CB 0%, #A4BCE0 35%, #FFD377 70%, #ECA1A6 100%)',
    ]
  },
  afternoon: {
    start: 11,
    end: 16,
    gradients: [
      // Bright, saturated blues and turquoise—midday sun
      'linear-gradient(120deg, #56CCF2 0%, #2F80ED 25%, #38F9D7 60%, #43CEA2 100%)',
      'linear-gradient(120deg, #2096FF 0%, #05FFA3 50%, #F9F47A 90%)',
      'linear-gradient(115deg, #2193b0 0%, #6dd5ed 40%, #b2fefa 80%, #f7797d 100%)',
    ]
  },
  evening: {
    start: 16,
    end: 20,
    gradients: [
      // Burnt orange to purple sunset
      'linear-gradient(120deg, #b24592 0%, #f15f79 35%, #ff5858 60%, #ffc371 90%)',
      'linear-gradient(120deg, #654ea3 0%, #eaafc8 40%, #ff9068 100%)',
      'linear-gradient(115deg, #2c3e50 0%, #fd746c 40%, #ff9068 78%, #b721ff 100%)',
    ]
  },
  night: {
    start: 20,
    end: 5,
    gradients: [
      // Deep blues, cosmic purples—moving clouds/moon haze
      'linear-gradient(120deg, #142850 0%, #27496d 22%, #0c7b93 50%, #00a8cc 100%)',
      'linear-gradient(120deg, #141E30 0%, #355C7D 46%, #6C5B7B 80%, #232526 100%)',
      'linear-gradient(120deg, #232526 0%, #0f2027 35%, #5c258d 76%, #485563 100%)',
    ]
  },
} as const;

// Chooses appropriate slot
function getCurrentTimeSlot(): keyof typeof TIME_SLOTS {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 16) return 'afternoon';
  if (hour >= 16 && hour < 20) return 'evening';
  return 'night';
}

// Used to provide (mild) variety
function getSessionNumber() {
  const today = new Date().toISOString().split('T')[0];
  const key = `bg-session-count-${today}`;
  let count = Number(localStorage.getItem(key)) || 0;
  count += 1;
  localStorage.setItem(key, String(count));
  return count;
}

// Our "background info" acts as before, but url = gradient string
export interface TimeAwareBackgroundInfo {
  url: string; // Actually CSS gradient now!
  photographer: string;
  photographerUrl: string;
  date: string;
  timeSlot: string;
  location?: { title: string };
  animated?: boolean; // For consumers to apply correct animation class
}

export const useTimeAwareBackground = () => {
  const [backgroundInfo, setBackgroundInfo] = useState<TimeAwareBackgroundInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const update = () => {
      setIsLoading(true);
      setError(null);
      const slot = getCurrentTimeSlot();
      const slotData = TIME_SLOTS[slot];
      const sessionNum = getSessionNumber();

      const hour = new Date().getHours();
      const gradIdx = ((sessionNum + hour) % slotData.gradients.length);
      const gradient = slotData.gradients[gradIdx];

      // Animate with a different speed and direction for each time of day
      if (!document.getElementById('lovable-bg-gradient-anim')) {
        const style = document.createElement('style');
        style.id = 'lovable-bg-gradient-anim';
        style.innerHTML = `
          .lovable-animated-bg-morning {
            background-size: 200% 200%;
            animation: lovable-gradient-move-morning 9s ease-in-out infinite alternate;
            transition: background 0.9s cubic-bezier(.52,.47,.38,.93);
          }
          .lovable-animated-bg-afternoon {
            background-size: 280% 180%;
            animation: lovable-gradient-move-afternoon 10s linear infinite alternate;
            transition: background 0.8s cubic-bezier(.42,.17,.11,.87);
          }
          .lovable-animated-bg-evening {
            background-size: 220% 220%;
            animation: lovable-gradient-move-evening 11s cubic-bezier(.4,.2,.7,1.2) infinite alternate;
            transition: background 0.8s cubic-bezier(.58,.21,.11,.97);
          }
          .lovable-animated-bg-night {
            background-size: 300% 230%;
            animation: lovable-gradient-move-night 14s ease-in-out infinite alternate;
            transition: background 1s cubic-bezier(.2,.7,.36,.84);
          }

          @keyframes lovable-gradient-move-morning {
            0% { background-position: 80% 40%; }
            100% { background-position: 10% 80%; }
          }
          @keyframes lovable-gradient-move-afternoon {
            0% { background-position: 0% 100%; }
            100% { background-position: 100% 30%; }
          }
          @keyframes lovable-gradient-move-evening {
            0% { background-position: 0% 0%; }
            100% { background-position: 100% 100%; }
          }
          @keyframes lovable-gradient-move-night {
            0% { background-position: 60% 70%; }
            100% { background-position: 20% 20%; }
          }
        `;
        document.head.appendChild(style);
      }

      setBackgroundInfo({
        url: gradient,
        photographer: 'Lovable AI',
        photographerUrl: 'https://lovable.so',
        date: new Date().toISOString().split('T')[0],
        timeSlot: slot,
        location: { title: `${slot.charAt(0).toUpperCase() + slot.slice(1)} Animated Gradient` },
        animated: true // Use this to apply the correct .lovable-animated-bg-[timeSlot] class
      });
      setIsLoading(false);
    };

    update();
    const t = setInterval(update, 60 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  return { backgroundInfo, isLoading, error };
};

// Still provide old export for compatibility
export const useUnsplashBackground = () => {
  return useTimeAwareBackground();
};
